import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  AddressElement,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  VStack,
  Text,
} from "@chakra-ui/react";
import { useRouter } from "next/router";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { useAPI } from "@/contexts/APIContext";
import { useUserData } from "@/contexts/UserDataContext";
import { ENTERPRISE_PLANS } from "@/utils/data/products";

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY);

const CheckoutForm = ({ txid }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    const result = await stripe.confirmPayment({
      //`Elements` instance that was used to create the Payment Element
      elements,
      confirmParams: {
        return_url: `${process.env.NEXT_PUBLIC_DOMAIN}/pay/success?txid=${txid}`,
      },
    });
    setLoading(false);

    if (result.error) {
      // TODO: Show error to your customer (for example, payment details incomplete)
      console.log(result.error);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <AddressElement options={{ mode: "billing" }} />
      {/* <Box minH={10} /> */}
      <PaymentElement />
      <Flex mt={4}>
        <Spacer />
        <Button
          colorScheme="green"
          rightIcon={<ChevronRightIcon />}
          type="submit"
          isLoading={loading}
          isDisabled={!stripe || !elements}
        >
          Pay
        </Button>
      </Flex>
    </form>
  );
};

const PIEnterpriseDetails = ({ paymentIntentId }) => {
  const api = useAPI();
  const { currentUser } = useUserData();

  const [paymentIntent, setPaymentIntent] = useState(undefined);

  useEffect(() => {
    if (!paymentIntentId) {
      return;
    }

    api
      .getUnprotected("/stripe/payment-intent", currentUser, {
        paymentIntentId,
      })
      .then((data) => {
        setPaymentIntent(data.paymentIntent);
      })
      .catch(() => {
        setPaymentIntent(undefined);
      });
  }, [paymentIntentId]);

  if (!paymentIntentId || !paymentIntent) {
    return null;
  }

  const { enterprisePlanSku } = paymentIntent;

  const enterprisePlan = ENTERPRISE_PLANS.find((p) => p.sku === enterprisePlanSku);

  return (
    <Box p={3} borderWidth="1px" borderRadius="md" overflow="hidden" mb={4}>
      <VStack spacing={3} align="stretch">
        <Heading as="h5" size="sm">
          Details
        </Heading>
        <Text fontSize="md">Description: {enterprisePlan.title}</Text>
        <Text fontSize="md">Amount: ${enterprisePlan.price}</Text>
      </VStack>
    </Box>
  );
};

const getPaymentIntentId = (secret) => {
  if (!secret || typeof secret !== "string") {
    return;
  }

  return secret.split("_secret")[0];
};

const ModalEnterpriseCheckoutForm = () => {
  const router = useRouter();
  const [clientSecret, setClientSecret] = useState();
  // const [enterpriseSku, setEnterpriseSku] = useState();

  useEffect(() => {
    const { clientSecret, enterprise_sku } = router.query;

    setClientSecret(clientSecret);
    // setEnterpriseSku(enterprise_sku);
  }, [router]);

  const paymentIntentId = getPaymentIntentId(clientSecret);

  if (!clientSecret) {
    return;
  }

  return (
    <Modal size="xl" isOpen={true} onClose={() => router.push("?clientSecret=")}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="3xl" fontFamily="heading">
          Purchase Enterprise Plan
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <PIEnterpriseDetails paymentIntentId={paymentIntentId} />
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CheckoutForm txid={clientSecret} />
          </Elements>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ModalEnterpriseCheckoutForm;
